import React, { FC } from 'react';
import { Icon, ActivityIndicator } from '@kontentino/ui';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import PermissionsGateButton from 'app/components/PermissionsGateButton';
import { ApprovalThreadType } from '../types';
import { getThreadApprovePermissions } from '../utils/approvalPermissions';

type Props = {
  thread: ApprovalThreadType;
  isLoading: boolean;
  onApprove: () => void;
  onRework: () => void;
};

const ApprovalActionButtons: FC<Props> = ({
  thread,
  isLoading,
  onApprove,
  onRework,
}) => {
  const { t } = useTranslation();
  const approvePermissions = getThreadApprovePermissions(thread);

  if (isLoading) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center">
        <ActivityIndicator />
      </div>
    );
  }

  const approveLabel =
    thread === 'internal'
      ? t('commentsWorkflowActions.internallyApprove')
      : t('commentsWorkflowActions.approve', 'Approve');

  const reworkLabel =
    thread === 'internal'
      ? t('commentsWorkflowActions.internallyReject')
      : t('commentsWorkflowActions.rework', 'Rework');

  return (
    <div className="tw-flex tw-gap-3 tw-border-t tw-border-grayscale-10 tw-p-4">
      <PermissionsGateButton
        scopes={approvePermissions}
        variant="success"
        size="large"
        iconBefore={<Icon icon={faCheck} />}
        onClick={onApprove}
        className="tw-flex-1"
        data-name="approvals_approve-button"
        data-cy="approvals_approve-button"
      >
        {approveLabel}
      </PermissionsGateButton>
      <PermissionsGateButton
        scopes={approvePermissions}
        variant="danger"
        size="large"
        iconBefore={<Icon icon={faXmark} />}
        onClick={onRework}
        className="tw-flex-1"
        data-cy="approvals_rework-button"
        data-name="approvals_rework-button"
      >
        {reworkLabel}
      </PermissionsGateButton>
    </div>
  );
};

export default ApprovalActionButtons;
